/**
 * Reports where the viewer is, and remembers it across devices.
 *
 * ## Why `fetch(…, {keepalive:true})` and not `sendBeacon`
 *
 * The plan named `navigator.sendBeacon` for the final report on unload. It cannot be used
 * here, for a reason that only shows up once auth exists: **sendBeacon sends no custom
 * headers**. This API is authenticated with `Authorization` and scoped with
 * `X-Profile-Id`, so a beacon would force the access token into the request body — a
 * strictly worse place for it — and the server would need a second, weaker way to
 * authenticate the same write.
 *
 * `fetch` with `keepalive:true` is the modern equivalent: the browser keeps the request
 * alive after the page goes away, exactly as a beacon does, while carrying the ordinary
 * headers. The payload cap is 64 KB; ours is about 150 bytes.
 *
 * ## Why `visibilitychange` and not `unload`
 *
 * `unload` does not fire reliably on mobile — a backgrounded tab is often killed without
 * it — and using it disables the browser's back/forward cache. `visibilitychange` fires
 * in every case that matters, including switching apps on a phone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { WatchProgressDto } from '@filmodasi/shared';
import { api, apiUrl, currentAccessToken, deviceId, packagedHeaders } from '../api/client';

/** Matches the plan. Frequent enough to survive a crash, rare enough to be free. */
const REPORT_INTERVAL_MS = 10_000;

// `deviceId` lives in the API client. It used to be defined here too, against the same
// storage key — two generators for one identity, which would have diverged the first time
// either was touched.

export interface WatchProgressOptions {
  itemId: string | null;
  /** Live has no position to resume; reporting one is refused server-side anyway. */
  enabled: boolean;
  /** Absolute seconds into the item — the remux offset is already added by the caller. */
  currentTime: () => number;
  durationSec: () => number | null;
}

/**
 * Loads the stored position once and keeps reporting the current one.
 *
 * Returns the resume point as a promise-free ref rather than state: the player asks for
 * it exactly once, when the media element is ready, and re-rendering the whole page
 * because a number arrived would restart the engine effect.
 */
export function useWatchProgress(opts: WatchProgressOptions) {
  const resumeRef = useRef<number | null>(null);
  /**
   * State, not a ref, and that matters: the player must not attach its engine before the
   * stored position is known, or the film starts at 0:00 and then jumps. The page gates
   * its spinner on this, so the engine effect runs exactly once, already knowing where
   * to start.
   */
  const [loaded, setLoaded] = useState(false);
  const currentTimeRef = useRef(opts.currentTime);
  const durationRef = useRef(opts.durationSec);
  currentTimeRef.current = opts.currentTime;
  durationRef.current = opts.durationSec;

  const { itemId, enabled } = opts;

  const report = useCallback(
    (keepalive: boolean) => {
      if (!itemId || !enabled) return;
      const positionSec = Math.floor(currentTimeRef.current());
      // Zero is not worth a write: it is what a player reports before it has started,
      // and storing it would overwrite a real position with nothing.
      if (!Number.isFinite(positionSec) || positionSec <= 0) return;

      const body = {
        itemId,
        positionSec,
        durationSec: durationRef.current(),
        clientUpdatedAt: new Date().toISOString(),
        deviceId: deviceId(),
      };

      if (keepalive) {
        // Bypasses the api() helper deliberately: this fires as the page is going away,
        // and a 401 → refresh → retry cycle would not survive the navigation. A dropped
        // final report costs at most ten seconds of accuracy.
        void fetch(apiUrl('/api/progress'), {
          method: 'PUT',
          keepalive: true,
          credentials: 'include',
          headers: authHeaders(),
          body: JSON.stringify(body),
        }).catch(() => {});
        return;
      }

      void api<{ progress: WatchProgressDto; applied: boolean }>('/api/progress', {
        method: 'PUT',
        body,
      }).catch(() => {
        /* progress is best-effort — never interrupt playback for it */
      });
    },
    [itemId, enabled],
  );

  /**
   * Fired on pause and after a seek, where the position genuinely changed.
   *
   * A `useCallback` rather than a fresh `() => report(false)` per render, because the
   * caller attaches it as a DOM listener in an effect. As a new closure each render it
   * could only be attached once, on mount — and then it stayed bound to the *first*
   * `report`, whose `itemId` was baked in. Playing a second film without the `<video>`
   * element being remounted would have written the new film's position against the old
   * film's id on every pause.
   */
  const flush = useCallback(() => report(false), [report]);

  // Fetch the stored position once per item.
  useEffect(() => {
    resumeRef.current = null;
    setLoaded(false);
    if (!itemId || !enabled) {
      setLoaded(true);
      return;
    }

    let cancelled = false;
    void api<WatchProgressDto | null>(`/api/progress/${itemId}`)
      .then((progress) => {
        if (cancelled) return;
        // A finished film restarts from the top; offering to resume the credits is
        // worse than not offering at all.
        resumeRef.current =
          progress && !progress.completed && progress.positionSec > 0 ? progress.positionSec : null;
      })
      .catch(() => {
        /* no stored position is indistinguishable from a failed read, and both mean
           "start from the beginning" */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [itemId, enabled]);

  // Periodic reports, plus one on the way out.
  useEffect(() => {
    if (!itemId || !enabled) return;

    const timer = setInterval(() => report(false), REPORT_INTERVAL_MS);
    const onHidden = () => {
      if (document.visibilityState === 'hidden') report(true);
    };
    document.addEventListener('visibilitychange', onHidden);
    // `pagehide` covers the bfcache path, where `visibilitychange` may not fire.
    window.addEventListener('pagehide', onHidden);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onHidden);
      // Leaving the player is the most accurate report there is.
      report(true);
    };
  }, [itemId, enabled, report]);

  return {
    /** Stored position, or null. Only meaningful once `loaded` is true. */
    resumeAt: () => resumeRef.current,
    loaded,
    flush,
  };
}

/**
 * The headers `api()` would have added.
 *
 * Duplicated rather than exported from the client because the keepalive path must not
 * go through the refresh-and-retry logic: it runs while the page is being torn down.
 */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...packagedHeaders(),
  };
  const token = currentAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const profileId = localStorage.getItem('filmodasi.profileId');
  if (profileId) {
    headers['x-profile-id'] = profileId;
    const grant = sessionStorage.getItem(`filmodasi.grant.${profileId}`);
    if (grant) headers['x-profile-token'] = grant;
  }
  return headers;
}
